// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Popover, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@imprivata-cloud/components';
import AuthenticatorList from '../../../../components/authenticatorList/AuthenticatorList';
import type { AuthenticatorCardProps } from '../../../../components/authenticatorList/authenticatorCard/AuthenticatorCard';
import { useSession } from '../../../../common/authorization/context/Session';
import getRelativeTime from '../../../../common/authMethodInfo/relativeTime';
import mapFactorTypeToNameAndIcon from '../../../../common/authMethodInfo/authMethodInfo';
import { useUserAuthenticators, useUnenrollAuthenticator } from '../hooks/useAuthenticators';
import QuestionImg from '../../../../assets/icons/question.svg?react';

export interface MenuButtonProps {
  enableMenu: boolean;
  menuIcon?: React.ReactNode;
  menuTitle?: string;
  menuClick?: () => void;
  userId: string;
}

const TRANSLATION_ROOT = 'portal.settings.users.authenticators';

export const RowMenuButton = ({ menuIcon, menuTitle, userId }: MenuButtonProps) => {
  const { tenantId } = useSession();
  const { t } = useTranslation();
  const [selectedAuthenticatorId, setSelectedAuthenticatorId] = useState<string | null>(null);
  const [removeDialogVisible, setRemoveDialogVisible] = useState(false);
  const [popUpIsOpen, setPopUpIsOpen] = useState(false);
  const { data, isFetching } = useUserAuthenticators(userId, tenantId ?? '', popUpIsOpen);
  const { mutate } = useUnenrollAuthenticator(tenantId ?? '', {});

  const authenticatorCards = useMemo(() => {
    if (!data || !popUpIsOpen) {
      return [];
    }
    return data.map((auth) => {
      const { name, icon } = mapFactorTypeToNameAndIcon(auth.factorType);
      return {
        title: name,
        icon,
        subtitle: getRelativeTime(auth.lastUsedAt),
        data: auth,
        onRemove: () => {
          setSelectedAuthenticatorId(auth.authenticatorId);
          setRemoveDialogVisible(true);
          setPopUpIsOpen(false);
        },
      };
    });
  }, [data, popUpIsOpen]);

  const handleOpenChange = (visible: boolean) => {
    if (visible) {
      setPopUpIsOpen(true);
    } else {
      setPopUpIsOpen(false);
    }
  };

  const handleConfirmRemove = () => {
    if (selectedAuthenticatorId) {
      mutate(selectedAuthenticatorId);
      setRemoveDialogVisible(false);
      setSelectedAuthenticatorId(null);
    }
  };

  return (
    <>
      <Modal
        open={removeDialogVisible}
        centered={true}
        onOk={handleConfirmRemove}
        okText={t(`${TRANSLATION_ROOT}.remove`)}
        onCancel={() => setRemoveDialogVisible(false)}
        cancelText={t(`${TRANSLATION_ROOT}.dont-remove`)}
      >
        <QuestionImg style={{ width: '4rem', margin: '1rem' }} />
        <h3>{t(`${TRANSLATION_ROOT}.remove-title`)}</h3>
        <p style={{ margin: '2rem' }}>{t(`${TRANSLATION_ROOT}.remove-message`)}</p>
      </Modal>

      <Popover
        content={
          isFetching ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <Spin data-testid={'loading-indicator'} size="small" />
            </div>
          ) : (
            <AuthenticatorList
              title={t(`${TRANSLATION_ROOT}.title`)}
              cards={authenticatorCards}
              emptyMessage={t(`${TRANSLATION_ROOT}.empty`)}
            />
          )
        }
        title={menuTitle}
        trigger="click"
        placement="bottomRight"
        // Needed to close the popover when the modal opens
        open={popUpIsOpen}
        onOpenChange={handleOpenChange}
      >
        <Button type="text" onClick={() => setPopUpIsOpen(true)} icon={menuIcon} aria-label="row-menu" />
      </Popover>
    </>
  );
};

export default RowMenuButton;
